import {
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    type ConnectedPosition,
} from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    Injector,
    input,
    output,
    signal,
    viewChild,
    type ElementRef,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { NavigationStart, Router, RouterLink } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { AuthService, injectOrg, injectUser } from '@quipex/core/auth';
import { APP_CONFIG_TOKEN, createContactUsHref } from '@quipex/core/config';
import {
    AddressPipe,
    BuildingNamePipe,
    QpxAvatar,
    QpxButton,
    QpxIcon,
    QpxLogo,
    QpxTooltip,
    Skeleton,
} from '@quipex/shared/ui';
import {
    ADDRESS_MODE,
    toAddressFormat,
    toBuildingNameFormat,
} from '@quipex/shared/utils';
import { distinctUntilChanged, filter, map, merge, skip } from 'rxjs';
import {
    AppTopBarScopeRegistry,
    injectAppTopBarEligibility,
    type AppTopBarCloseReason,
    type AppTopBarModuleMenuContext,
    type AppTopBarScopePortal,
    type AppTopBarShortcut,
} from './app-top-bar-runtime';

export type AppTopBarShortcutsByScope = Readonly<
    Partial<Record<AppTopBarScopePortal['kind'], readonly AppTopBarShortcut[]>>
>;

interface AppTopBarPanelSession {
    readonly generation: number;
    readonly origin: CdkOverlayOrigin;
}

interface ScopeIdentity {
    readonly kind: AppTopBarScopePortal['kind'];
    readonly buildingId: string | null;
}

const PANEL_POSITIONS = [
    {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
        offsetY: 1,
    },
    {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom',
        offsetY: -1,
    },
] satisfies readonly ConnectedPosition[];

const FOCUSABLE_SELECTOR = [
    '[data-app-top-bar-autofocus]',
    'input:not([disabled])',
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

@Component({
    selector: 'qpx-app-top-bar',
    imports: [
        AddressPipe,
        BuildingNamePipe,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        MatButtonModule,
        MatBadgeModule,
        MatMenuModule,
        NgIcon,
        NgTemplateOutlet,
        QpxAvatar,
        QpxButton,
        QpxIcon,
        QpxLogo,
        QpxTooltip,
        RouterLink,
        Skeleton,
    ],
    templateUrl: './app-top-bar.html',
    styleUrl: './app-top-bar.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppTopBar {
    //#region Dependency injection
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly authService = inject(AuthService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly eligibility = injectAppTopBarEligibility();
    private readonly injector = inject(Injector);
    private readonly organisation = injectOrg();
    private readonly router = inject(Router);
    private readonly scopeRegistry = inject(AppTopBarScopeRegistry);
    private readonly user = injectUser();
    //#endregion

    //#region Constants
    protected readonly panelPositions = PANEL_POSITIONS;
    protected readonly contactUsHref = createContactUsHref(
        this.appConfig.supportEmail
    );
    protected readonly inAppHelpSupportAvailable = this.contactUsHref !== null;
    //#endregion

    //#region Inputs
    public readonly hasUnreadReleaseNotes = input(false);
    public readonly manageCompanyShortcut = input<AppTopBarShortcut | null>(
        null
    );
    public readonly shortcutsByScope = input<AppTopBarShortcutsByScope>({});
    //#endregion

    //#region Outputs
    public readonly findBuildingRequested = output<HTMLElement>();
    public readonly whatsNewRequested = output<void>();
    //#endregion

    //#region User state
    protected readonly me = this.user.required;
    protected readonly org = this.organisation.required;
    protected readonly userInitials = this.user.initials;
    protected readonly userMenuLabel = computed(
        () => `Open user menu for ${this.me().displayName}, ${this.org().name}`
    );
    //#endregion

    //#region State
    protected readonly activeScope = this.scopeRegistry.activeScope;
    protected readonly activePanel = signal<AppTopBarPanelSession | null>(null);
    protected readonly moduleSelectorLoading = computed(
        () => !this.activeScope()
    );
    protected readonly buildingSelectorLoading = this.moduleSelectorLoading;
    protected readonly buildingFallbackLabel = computed(() =>
        this.activeScope()?.kind === 'company'
            ? this.org().name
            : 'No building selected'
    );
    protected readonly moduleLabel = computed(
        () => this.activeScope()?.moduleTriggerLabel ?? 'Modules'
    );
    protected readonly buildingSelectorLabel = computed(() => {
        if (this.buildingSelectorLoading()) return 'Building selector loading';

        const building = this.activeScope()?.currentBuilding;
        const identity = building
            ? [
                  toBuildingNameFormat(building).trim(),
                  toAddressFormat(building.address, ADDRESS_MODE.City).trim(),
              ]
                  .filter(Boolean)
                  .join(', ')
            : `${this.buildingFallbackLabel()}, Choose a building`;

        return `Open building selector: ${identity}`;
    });
    protected readonly moduleSelectorLabel = computed(() =>
        this.moduleSelectorLoading()
            ? 'Module selector loading'
            : `Open module selector: ${this.moduleLabel()}`
    );
    protected readonly moduleIcon = computed(
        () => this.activeScope()?.moduleTriggerIcon ?? 'lucideGrid2X2'
    );
    protected readonly moduleClassName = computed(
        () => this.activeScope()?.moduleTriggerClassName
    );
    protected readonly modulePanelOpen = computed(
        () => this.activePanel() !== null
    );
    protected readonly moduleMenuContext =
        computed<AppTopBarModuleMenuContext | null>(() => {
            const scope = this.activeScope();
            if (!scope) return null;

            return {
                close: this.closeFromTemplate,
                shortcuts: this.shortcutsByScope()[scope.kind] ?? [],
            };
        });
    private generation = 0;
    private pendingDetachGeneration: number | null = null;
    private resizeObserver: ResizeObserver | null = null;
    //#endregion

    //#region Template references
    private readonly connectedOverlay =
        viewChild<CdkConnectedOverlay>('connectedOverlay');
    private readonly panelElement = viewChild<ElementRef<HTMLElement>>('panel');
    private readonly userMenuTrigger = viewChild(MatMenuTrigger);
    //#endregion

    constructor() {
        const scopeIdentity$ = toObservable(
            computed<ScopeIdentity | null>(() => {
                const scope = this.activeScope();
                if (!scope) return null;

                return {
                    kind: scope.kind,
                    buildingId: scope.currentBuilding?.id ?? null,
                };
            })
        ).pipe(
            distinctUntilChanged(
                (previous, current) =>
                    previous?.kind === current?.kind &&
                    previous?.buildingId === current?.buildingId
            ),
            skip(1),
            map(() => 'scope-change' as const)
        );
        const eligibility$ = toObservable(this.eligibility).pipe(
            distinctUntilChanged(),
            skip(1),
            filter((eligible) => !eligible),
            map(() => 'eligibility-change' as const)
        );
        const navigation$ = this.router.events.pipe(
            filter(
                (event): event is NavigationStart =>
                    event instanceof NavigationStart
            ),
            map(() => 'navigation' as const)
        );

        merge(scopeIdentity$, eligibility$, navigation$)
            .pipe(takeUntilDestroyed())
            .subscribe((reason) => this.close(reason));

        this.destroyRef.onDestroy(() => {
            this.disconnectResizeObserver();
            this.close('eligibility-change');
        });
    }

    //#region Template methods
    protected requestFindBuilding(event: MouseEvent): void {
        const origin = event.currentTarget;
        if (!(origin instanceof HTMLElement)) return;

        this.close('detach');
        this.findBuildingRequested.emit(origin);
    }

    protected toggleModulePanel(origin: CdkOverlayOrigin): void {
        if (!this.activeScope()) return;
        this.togglePanel(origin);
    }

    protected closeForOutsideClick(): void {
        this.close('outside');
    }

    protected onPanelKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            this.close('escape');
        }
    }

    protected onPanelFocusOut(event: FocusEvent): void {
        const panel = event.currentTarget;
        if (!(panel instanceof HTMLElement)) return;

        const nextTarget = event.relatedTarget;
        if (nextTarget === null) return;
        if (nextTarget instanceof Node && panel.contains(nextTarget)) return;

        this.close('tab');
    }

    protected onOverlayAttached(): void {
        this.pendingDetachGeneration = null;

        afterNextRender(
            () => {
                this.observePanelSize();
                this.connectedOverlay()?.overlayRef.updatePosition();
                this.panelElement()
                    ?.nativeElement.querySelector<HTMLElement>(
                        FOCUSABLE_SELECTOR
                    )
                    ?.focus();
            },
            { injector: this.injector }
        );
    }

    protected onOverlayDetached(): void {
        this.disconnectResizeObserver();
        const detachedGeneration = this.pendingDetachGeneration;
        this.pendingDetachGeneration = null;

        if (
            detachedGeneration !== null &&
            this.activePanel()?.generation === detachedGeneration
        ) {
            this.activePanel.set(null);
        }
    }

    protected closeForUserMenu(): void {
        this.close('detach');
    }

    protected requestWhatsNew(): void {
        this.userMenuTrigger()?.closeMenu();
        this.whatsNewRequested.emit();
    }

    protected signOut(): void {
        this.authService.logout();
    }
    //#endregion

    //#region Private methods
    private readonly closeFromTemplate = (
        reason: AppTopBarCloseReason
    ): void => {
        this.close(reason);
    };

    private togglePanel(origin: CdkOverlayOrigin): void {
        this.userMenuTrigger()?.closeMenu();
        const current = this.activePanel();
        if (current) {
            this.close('toggle');
            return;
        }

        this.generation += 1;
        this.pendingDetachGeneration = null;
        this.activePanel.set({ origin, generation: this.generation });
    }

    private close(reason: AppTopBarCloseReason): void {
        const panel = this.activePanel();
        if (!panel) return;

        this.pendingDetachGeneration = panel.generation;
        this.activePanel.set(null);
        this.disconnectResizeObserver();

        if (shouldRestoreOriginFocus(reason)) {
            afterNextRender(
                () => panel.origin.elementRef.nativeElement.focus(),
                {
                    injector: this.injector,
                }
            );
        }
    }

    private observePanelSize(): void {
        this.disconnectResizeObserver();
        const panel = this.panelElement()?.nativeElement;
        if (!panel || typeof ResizeObserver === 'undefined') return;

        this.resizeObserver = new ResizeObserver(() => {
            this.connectedOverlay()?.overlayRef.updatePosition();
        });
        this.resizeObserver.observe(panel);
    }

    private disconnectResizeObserver(): void {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }
    //#endregion
}

function shouldRestoreOriginFocus(reason: AppTopBarCloseReason): boolean {
    return reason === 'escape' || reason === 'outside' || reason === 'toggle';
}
